import { formatSnakeValue } from 'src/lib/formatters'

import { analyzeCoverage } from '../employment/lib/coverage'

import { formatDateString } from './formatters'
import { filterPrivateQuestionResponses } from './questionResponses'
import type { SectionKey } from './stepChecks'

export interface ReviewSection {
  key: SectionKey
  title: string
  path: string
  summary: string
}

const SEPARATOR = ' · '

const joinPresent = (parts: Array<string | null | undefined>) =>
  parts.filter((part) => part && part.trim()).join(SEPARATOR)

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime())

const formatDay = (value: unknown) =>
  isValidDate(value) ? formatDateString(value) : ''

const summarizePersonal = (values) =>
  joinPresent([
    [values.firstName, values.lastName].filter(Boolean).join(' '),
    values.email,
  ]) || 'Nothing added yet'

const summarizeAddresses = (values) => {
  const addresses = (values.addresses ?? []).filter(
    (address) => address.street || address.city
  )
  if (addresses.length === 0) return 'No addresses added'
  const current = addresses.find((address) => !address.endDate) ?? addresses[0]
  const line = [current.street, current.city, current.state]
    .filter(Boolean)
    .join(', ')
  const others = addresses.length - 1
  return others > 0 ? `${line}${SEPARATOR}+${others} more` : line
}

const licenseClassLabel = (licenseClass?: string) => {
  if (!licenseClass) return ''
  if (licenseClass === 'NO_CDL') return 'No CDL'
  return licenseClass.length <= 2
    ? `Class ${licenseClass}`
    : formatSnakeValue(licenseClass)
}

const summarizeLicense = (values) => {
  const license = values.license ?? {}
  if (!license.licenseNumber && !license.licenseClass) return 'No license added'
  const expires = formatDay(license.expirationDate)
  return joinPresent([
    licenseClassLabel(license.licenseClass),
    license.state,
    expires ? `Expires ${expires}` : '',
  ])
}

const summarizeEquipment = (values) => {
  const equipment = (values.equipment ?? []).filter(
    (item) => item.type || item.make || item.model
  )
  if (equipment.length === 0) return 'None reported'
  const labels = equipment.map((item) =>
    item.type
      ? formatSnakeValue(item.type)
      : [item.make, item.model].filter(Boolean).join(' ')
  )
  return [...new Set(labels)].join(SEPARATOR)
}

const summarizeAccidents = (values) => {
  const count = (values.accidents ?? []).length
  return count === 0 ? 'None reported' : `${count} reported`
}

const formatMonths = (months: number) => {
  const years = Math.floor(months / 12)
  const rest = months % 12
  const parts = []
  if (years > 0 || rest === 0)
    parts.push(`${years} yr${years === 1 ? '' : 's'}`)
  if (rest > 0) parts.push(`${rest} mo`)
  return parts.join(' ')
}

const summarizeEmployment = (
  values,
  requiredYears: number | null | undefined,
  now: Date
) => {
  const entries = (values.employmentExperiences ?? []).filter(
    (entry) => entry.isUnemployment || entry.companyName?.trim()
  )
  if (entries.length === 0) return 'Nothing added yet'
  const coverage = analyzeCoverage(entries, requiredYears, now)
  const covered = formatMonths(coverage.coveredMonths)
  const enforced = Boolean(requiredYears && requiredYears > 0)
  return enforced
    ? `${covered} of ${requiredYears} yrs covered`
    : `${covered} covered`
}

const isAnswered = (response) =>
  Boolean(response.valueShortText?.trim()) ||
  Boolean(response.valueLongText?.trim()) ||
  response.valueBoolean === true ||
  response.valueBoolean === false ||
  (response.selectedOptions?.length ?? 0) > 0

const summarizeQuestions = (questionResponses) => {
  const total = questionResponses.length
  const answered = questionResponses.filter(isAnswered).length
  return `${answered} of ${total} answered`
}

export interface ReviewSummaryOptions {
  requiredYears?: number | null
  now?: Date
}

export const buildReviewSections = (
  values,
  questionResponses,
  { requiredYears = null, now = new Date() }: ReviewSummaryOptions = {}
): ReviewSection[] => {
  const privateQuestions = filterPrivateQuestionResponses(questionResponses)
  const sections: ReviewSection[] = [
    {
      key: 'personal',
      title: 'Personal',
      path: '',
      summary: summarizePersonal(values),
    },
    {
      key: 'addresses',
      title: 'Addresses',
      path: '/address-information',
      summary: summarizeAddresses(values),
    },
    {
      key: 'license',
      title: 'License',
      path: '/license-information',
      summary: summarizeLicense(values),
    },
    {
      key: 'equipment',
      title: 'Equipment',
      path: '/equipment-information',
      summary: summarizeEquipment(values),
    },
    {
      key: 'accidents',
      title: 'Accidents',
      path: '/accident-history',
      summary: summarizeAccidents(values),
    },
    {
      key: 'employment',
      title: 'Employment',
      path: '/employment-history',
      summary: summarizeEmployment(values, requiredYears, now),
    },
  ]
  if (privateQuestions.length > 0) {
    sections.push({
      key: 'questions',
      title: 'Questions',
      path: '/company-questions',
      summary: summarizeQuestions(privateQuestions),
    })
  }
  return sections
}
